/**
 * Preisautomatik im Monats-Editor (Board „Monat je Apartment“): bei mehreren Tagen „Preisautomatik
 * in der Auswahl“ mit Quelle und Ziel je Tag und „wieder automatisch“ je Tag und für die Auswahl;
 * bei einem Tag das ganze Tagesdetail der Preisautomatik. Nur mit Schicht (Admin/Staff); zurückgeben
 * nur mit Edit.
 */
import type { DynamicPricingCalendarDay } from '@/shared/api';
import { addIsoDays, diffIsoDays } from '../calendar/calendarDates';
import type { SelectedRange } from '../calendar/rangeSelectionModel';
import { useAutomationLayer, useAutomationStatus } from './AutomationLayerContext';
import { MonthDayDetail } from './MonthDayDetail';
import { SelectionAutomationDay } from './SelectionAutomationDay';
import { useRangeRelease } from './useRangeRelease';

interface Props {
  propertyGroupId: string;
  propertyId: string;
  ranges: readonly SelectedRange[];
  canEdit: boolean;
}

export function SelectionAutomation({ propertyGroupId, propertyId, ranges, canEdit }: Props) {
  const layer = useAutomationLayer();
  const status = useAutomationStatus();
  const release = useRangeRelease(layer?.mode(propertyId) === 'Off');
  const single = ranges.length === 1 && ranges[0]!.from === ranges[0]!.to ? ranges[0]!.from : null;
  // Keine Schicht und kein Fehler: die Rolle hat keine Preisautomatik, oder sie lädt noch.
  if (!layer && !status) return null;
  if (single) {
    return (
      <MonthDayDetail
        propertyGroupId={propertyGroupId}
        propertyId={propertyId}
        date={single}
        canEdit={canEdit}
      />
    );
  }
  if (!layer) {
    return (
      <p role="alert" className="bg-paper rounded-tile text-ink-soft p-3.5 text-[13px]">
        {status!.message}
      </p>
    );
  }
  const days = ranges
    .flatMap((range) => datesOf(range))
    .map((date) => layer.day(propertyId, date))
    .filter((day): day is DynamicPricingCalendarDay => day !== null);
  if (days.length === 0) return null;
  const manualRate = days.some((day) => day.rateSource === 'Manual');
  const manualStay = days.some((day) => day.minimumStaySource === 'Manual');

  return (
    <div className="bg-paper rounded-tile flex flex-col gap-2 p-3.5">
      <span className="text-ink-soft font-mono text-[10px] font-semibold uppercase tracking-[0.12em]">
        Preisautomatik in der Auswahl
      </span>
      {days.slice(0, 6).map((day) => (
        <SelectionAutomationDay
          key={day.date}
          day={day}
          onRelease={
            canEdit && (day.rateSource === 'Manual' || day.minimumStaySource === 'Manual')
              ? () =>
                  release.mutate([
                    {
                      propertyId,
                      from: day.date,
                      to: day.date,
                      rate: day.rateSource === 'Manual',
                      minimumStay: day.minimumStaySource === 'Manual',
                    },
                  ])
              : undefined
          }
          busy={release.isPending}
        />
      ))}
      {days.length > 6 && (
        <span className="text-ink-soft text-xs">… und {days.length - 6} weitere Tage</span>
      )}
      {canEdit && (manualRate || manualStay) && (
        <button
          type="button"
          disabled={release.isPending}
          onClick={() =>
            release.mutate(
              ranges.map((range) => ({
                propertyId,
                from: range.from,
                to: range.to,
                rate: manualRate,
                minimumStay: manualStay,
              })),
            )
          }
          className="text-forest min-h-11 self-start text-[13px] font-bold disabled:opacity-50"
        >
          Ganze Auswahl wieder automatisch steuern
        </button>
      )}
      <span className="text-ink-soft text-xs leading-relaxed">
        Setzt du einen Preis, übernimmst du diese Tage von Hand. Vor dem Senden zeigen wir, welche
        Tage wechseln.
      </span>
    </div>
  );
}

function datesOf(range: SelectedRange): string[] {
  const count = Math.min(diffIsoDays(range.from, range.to) + 1, 400);
  return Array.from({ length: count }, (_, offset) => addIsoDays(range.from, offset));
}
